import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Terminal, Zap } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1764258560163-198782335017?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjeWJlciUyMGRhdGElMjBzdHJlYW0lMjBuZW9uJTIwZGFya3xlbnwwfHx8fDE3Nzc1MjM2NDl8MA&ixlib=rb-4.1.0&q=85";

const ROTATE = [
  "custom software.",
  "cloud platforms.",
  "AI systems.",
  "legacy modernization.",
  "DevOps & SRE.",
  "contract engineering.",
];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ROTATE.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden border-b border-white/10"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/85 to-obsidian" />
      <div className="absolute inset-0 bg-grid opacity-60" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-36">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 border border-neon-green/30 px-3 py-1.5 bg-neon-green/[0.04]"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-neon-green animate-pulse" />
          <span className="chip text-neon-green">
            &gt; enterprise_it.services × products
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="mt-8 font-display font-black tracking-tighter leading-[0.95] text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[92px] max-w-5xl"
        >
          Your enterprise{" "}
          <span className="text-glow-green text-neon-green">engineering</span>{" "}
          partner. <br className="hidden sm:block" />
          Across{" "}
          <span className="relative inline-block min-w-[260px] lg:min-w-[420px] text-left align-bottom">
            <motion.span
              key={idx}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-block text-glow-cyan text-neon-cyan"
            >
              {ROTATE[idx]}
            </motion.span>
            <span className="caret ml-1 text-neon-cyan/70" />
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-8 text-white/70 max-w-2xl text-base md:text-lg leading-relaxed font-mono"
        >
          United Techlab is a full-stack IT services &amp; products company.
          Hire us to build new software, scale on cloud, modernize legacy,
          staff your teams, or deploy AI with enterprise-grade guardrails,
          with the same Goldman-Sachs-grade rigor we bring to every engagement.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-start-project"
            className="group inline-flex items-center gap-2 bg-neon-green text-obsidian px-6 py-3.5 text-sm font-mono font-bold uppercase tracking-widest hover:shadow-[0_0_40px_rgba(0,255,65,0.6)] hover:-translate-y-0.5 transition-all"
          >
            Start a Project
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/audit"
            data-testid="hero-cta-free-audit"
            className="group inline-flex items-center gap-2 border border-neon-cyan/60 text-neon-cyan px-6 py-3.5 text-sm font-mono font-bold uppercase tracking-widest hover:bg-neon-cyan hover:text-obsidian hover:shadow-[0_0_32px_rgba(0,255,255,0.5)] transition-all"
          >
            <Zap size={16} /> Free Risk Audit
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-explore-services"
            className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 text-sm font-mono font-semibold uppercase tracking-widest hover:border-white/40 transition-all"
          >
            Explore Services
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl"
        >
          {[
            { k: "services", v: "7 specialties" },
            { k: "products", v: "2 enterprise" },
            { k: "founder", v: "ex-Goldman" },
            { k: "model", v: "outcome-owned" },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-3 border border-white/10 bg-black/40 backdrop-blur-sm p-4"
            >
              <Terminal size={16} className="text-neon-green shrink-0" />
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[11px] font-mono text-white/50 truncate">&gt; {item.k}</span>
                <span className="text-sm text-white truncate">{item.v}</span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neon-green/60 to-transparent animate-scan-line pointer-events-none" />
    </section>
  );
}
